// src/components/InvoiceModal.jsx
//
// Invoice for one order: a prefilled email you can edit and send, and a print
// button.
//
// There is no mail server behind this app, so "send" hands the message to the
// machine's own mail client with everything already filled in. That keeps the
// sent copy in the shop's normal Sent folder and means no SMTP credentials to
// look after. If the customer's email was never taken, it can be typed here.

import { useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  invoiceSubject, invoiceEmailBody, emailForOrder, mailtoLink, isValidEmail,
  invoiceNumberOf, invoiceTotals,
} from '../lib/invoice';

const money = (n) => '$' + (Number(n) || 0).toFixed(2);

export function InvoiceModal({ order, onClose, onPrint }) {
  const { shop } = useAuth();
  const [to,      setTo]      = useState(() => emailForOrder(order));
  const [subject, setSubject] = useState(() => invoiceSubject(order, shop));
  const [body,    setBody]    = useState(() => invoiceEmailBody(order, shop));
  const [copied,  setCopied]  = useState('');
  const [note,    setNote]    = useState('');

  const tot = useMemo(() => invoiceTotals(order), [order]);
  const emailOk = isValidEmail(to);
  const hadEmailOnFile = !!emailForOrder(order);

  async function copy(text, what) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      setNote('Could not copy automatically — select the text and copy it.');
    }
  }

  // A real anchor rather than assigning window.location: it lets the shop
  // right-click to copy the address, doesn't fight browsers that block
  // programmatic navigation, and leaves the SPA untouched if the OS has no
  // mail client registered.
  const mailHref = useMemo(() => mailtoLink({ to, subject, body }), [to, subject, body]);

  function warnIfLong() {
    if (mailHref.length > 1900) {
      setNote('This invoice is long, so your mail app may cut it short. If that happens, use “Copy invoice text” and paste it in.');
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h2>Invoice {invoiceNumberOf(order)}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          background:'var(--parch)', border:'1px solid var(--border)',
          borderRadius:'var(--radius-sm)', padding:'9px 12px', marginBottom:14, fontSize:13,
        }}>
          <span>Order #{order.orderNumber} · {order.customerName || '—'}</span>
          <strong style={{ fontSize:15, color:'var(--g700)' }}>{money(tot.total)}</strong>
        </div>

        {note && (
          <div style={{
            background:'var(--gold-pale)', border:'1px solid #f5d9a0', color:'var(--gold)',
            borderRadius:'var(--radius-sm)', padding:'9px 12px', fontSize:12.5, marginBottom:12,
          }}>{note}</div>
        )}

        <div className="form-group">
          <label className="field-label">
            Email to {hadEmailOnFile
              ? <span style={{color:'var(--text-muted)',fontWeight:400}}>— from the order</span>
              : <span style={{color:'var(--gold)',fontWeight:600}}>— none on file, type one</span>}
          </label>
          <input
            className="input" type="email" value={to} onChange={e => setTo(e.target.value)}
            placeholder="customer@example.com" autoFocus={!hadEmailOnFile}
          />
          {to && !emailOk && (
            <div style={{ fontSize:12, color:'var(--red)', marginTop:4 }}>
              That doesn't look like a valid email address.
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="field-label">Subject</label>
          <input className="input" value={subject} onChange={e => setSubject(e.target.value)}/>
        </div>

        <div className="form-group">
          <label className="field-label">Message — edit anything before sending</label>
          <textarea
            className="input" rows={14} value={body} onChange={e => setBody(e.target.value)}
            style={{ fontFamily:'ui-monospace, Menlo, Consolas, monospace', fontSize:12.5, lineHeight:1.5 }}
          />
        </div>

        <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:-4, marginBottom:12 }}>
          Sending opens your mail app (Outlook, Gmail, Mail) with this filled in —
          check it over and press Send there.
        </p>

        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {emailOk ? (
            <a className="btn btn-primary" href={mailHref} onClick={warnIfLong}
              title="Open your mail app with this invoice">
              ✉ Send invoice
            </a>
          ) : (
            <button className="btn btn-primary" disabled
              title="Enter a valid email address first">
              ✉ Send invoice
            </button>
          )}
          <button className="btn btn-secondary" onClick={onPrint}>🖨 Print invoice</button>
          <button className="btn btn-secondary" onClick={() => copy(body, 'body')}>
            {copied === 'body' ? '✓ Copied' : 'Copy invoice text'}
          </button>
          <button className="btn btn-ghost" style={{ marginLeft:'auto' }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
